<template>
  <div class="google-signin-success">
    <div class="topic">
      <div class="back-button" @click="backButtonClick" title="Back">
        <IconArrowLeft />
      </div>
      <span>Welcome, {{ userInfo.name }}!</span>
      <img
        class="user-image"
        v-if="userInfo?.picture && !loadImageError"
        :src="userInfo.picture"
        @error="loadImageError = true"
      />
      <div v-else class="no-image">
        <div>
          <img src="@/assets/user.svg" />
        </div>
      </div>
    </div>

    <div class="message">
      You are signing in to
      <span class="domain-name">{{ subDomain }}.{{ hostname }}</span> for the
      first time and need to link your Google account to an
      {{ app.productName }} account. Please enter your
      {{ app.productName }} user ID and password to continue.
    </div>
    <div class="account-input">
      <label>User Id</label>
      <input
        ref="userIdInput"
        type="text"
        v-model="user.id"
        :disabled="isBusy"
      />
      <label>Password</label>
      <input type="password" v-model="user.password" :disabled="isBusy" />
      <button
        :disabled="!user.id || !user.password || isBusy"
        @click="linkAccount"
      >
        <Spinner v-if="isBusy" :width="'3px'" :size="'24px'" :color="'#fff'" />
        <span>Link Account</span>
      </button>
      <div class="error-message" v-show="errorMessage">
        <span v-if="errorMessage">{{ errorMessage }}</span>
      </div>
    </div>
    <div class="countdown-line">
      <div class="countdown-line-inner" :style="passwordCountDownStyle"></div>
    </div>
    <div class="sub-message">
      If you don't have an {{ app.productName }} account, please contact your
      administrator to create one for you.
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import IconArrowLeft from "@/components/Icons/IconArrowLeft.vue";
import Spinner from "@/components/Spinner.vue";
export default {
  name: "GoogleSignInMapping",
  components: {
    IconArrowLeft,
    Spinner,
  },
  props: {
    googleIdToken: {
      type: String,
      required: true,
    },
    userInfo: {
      type: Object,
      required: true,
    },
    subDomain: {
      type: String,
      required: true,
    },
    loginKey: {
      type: String,
      default: null,
    },
    tenantId: {
      type: String,
      default: null,
    },
    hostname: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loadImageError: false,
      user: {
        id: "",
        password: "",
      },
      errorMessage: null,
      isBusy: false,
      passwordTimer: null,
      passwordTimeoutCounter: 0,
    };
  },
  computed: {
    ...mapGetters(["app"]),
    passwordCountDownStyle() {
      return {
        width: `${((120 - this.passwordTimeoutCounter) / 120) * 100}%`,
      };
    },
  },
  methods: {
    ...mapActions(["__linkAccount"]),
    backButtonClick() {
      this.$emit("back");
    },
    async linkAccount() {
      this.errorMessage = null;
      this.isBusy = true;
      const payload = {
        domain: this.subDomain,
        idToken: this.googleIdToken,
        userId: this.user.id,
        password: this.user.password,
      };
      const ret = await this.__linkAccount(payload);
      if (ret == null || ret?.success === false) {
        this.errorMessage = "User not found or password incorrect";
      } else {
        this.$emit("account-linked", {
          loginKey: ret.loginKey,
          hostName: ret.hostName,
          tenantId: ret.tenantId,
        });
      }
      this.isBusy = false;
    },
    passwordTimeout() {
      if (this.passwordTimeoutCounter >= 120) {
        if (this.passwordTimer) {
          clearInterval(this.passwordTimer);
        }
        this.$emit("back");
        return;
      }
      this.passwordTimeoutCounter++;
    },
  },
  mounted() {
    this.passwordTimeoutCounter = 0;
    this.passwordTimer = setInterval(this.passwordTimeout, 1000);
    this.$nextTick(() => {
      // Focus on the user ID input field when the component is mounted
      this.$refs.userIdInput.focus();
    });
  },
  beforeDestroy() {
    if (this.passwordTimer) {
      clearInterval(this.passwordTimer);
    }
  },
  watch: {
    user: {
      handler(newValue) {
        if (newValue) {
          this.errorMessage = null; // Clear error when user ID is entered
        }
        if (newValue.id == null || newValue.id === "") {
          newValue.password = "";
        }
        this.passwordTimeoutCounter = 0;
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/styles/themeColor.scss";
.google-signin-success {
  text-align: center;
  padding: 0px 0px;

  .topic {
    width: 100%;
    margin-bottom: 20px;
    font-size: 22px;
    align-items: center;
    justify-items: center;
    color: $theme-color;
    display: grid;
    grid-template-columns: 42px 1fr 42px;
    column-gap: 5px;
    span {
      pointer-events: none;
    }
    .back-button {
      width: 42px;
      height: 42px;
      min-width: 42px;
      min-height: 42px;
      border-radius: 50%;
      display: grid;
      align-items: center;
      justify-items: center;
      cursor: pointer;
      svg {
        fill: $theme-color;
        width: 32px;
        height: 32px;
      }
      transition: background-color 0.15s ease;
      &:hover {
        background-color: $theme-color-alpha-20;
      }
    }
  }

  .user-image {
    pointer-events: none;
    user-select: none;
    border-radius: 50%;
    width: 42px;
    height: 42px;
    min-width: 42px;
    min-height: 42px;
    object-fit: cover;
  }
  .no-image {
    pointer-events: none;
    user-select: none;
    display: grid;
    place-items: center;
    width: 42px;
    div {
      width: 42px;
      height: 42px;
      border-radius: 50%;
      background-color: #eee;
      display: grid;
      align-items: end;
      justify-items: center;
      overflow: hidden;
      img {
        width: 100%;
        height: 100%;
        opacity: 0.1;
        object-fit: cover;
        transform: translateY(7px);
      }
    }
  }
  .message {
    text-align: start;
    margin-top: 40px;
    line-height: 1.4;
    color: #666;
    .domain-name {
      color: $theme-color;
      text-decoration: underline;
    }
  }
  .sub-message {
    text-align: start;
    margin-top: 30px;
    color: #999;
    font-size: 14px;
    line-height: 1.4;
    margin-bottom: 50px;
  }

  .account-input {
    margin-top: 30px;
    width: 100%;
    display: grid;
    justify-items: start;
    color: #999;
    user-select: none;
  }

  .label {
    font-size: 14px;
    pointer-events: none;
  }
  input {
    justify-self: stretch;
    font-size: 15px;
    min-height: 40px;
    padding: 0px 10px;
    margin-top: 10px;
    margin-bottom: 20px;
    border: 1px solid #999;
    border-radius: 4px;
    background-color: #fefefe;
  }
  input:focus {
    outline: 1px solid $theme-color-alpha-70;
    border: 1px solid $theme-color;
  }
  button:disabled {
    background-color: #ccc;
  }
  button {
    font-size: 14px;
    width: 100%;
    min-height: 42px;
    margin-top: 20px;
    padding: 0px 10px;
    border: 0px;
    border-radius: 4px;
    background-color: $theme-color;
    color: #ffffff;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 1px;
    word-spacing: 3px;
    display: flex;
    gap: 5px;
    align-items: center;
    justify-content: center;
  }
  button:disabled {
    pointer-events: none;
    opacity: 0.5;
  }
  button:focus {
    outline: 2px solid $theme-color-alpha-70;
  }

  .error-message {
    display: grid;
    width: 100%;
    margin: 10px 0px;
    align-items: center;
    justify-items: center;
    background-color: #ff0000;
    border-radius: 4px;
    span {
      color: #fff;
      font-size: 14px;
      text-align: center;
      padding: 5px 20px;
    }
  }
}

.countdown-line {
  margin-top: 20px;
  opacity: 0.3;
  position: relative;
  width: 100%;
  height: 1px;
  background-color: #ccc;
  margin-bottom: 10px;
  .countdown-line-inner {
    width: 0%;
    height: 100%;
    background-color: $theme-color;
  }
}
</style>
